'use client';

import {
  Button,
  Loader,
  MinthrSelect,
  Sidebar,
  TextArea,
} from 'components/common';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { useParams } from 'next/navigation';
import {
  CreateDemondeDocument,
  documentsTypelist,
  documentTypesService,
  GetCreateDocument,
} from '../services/document';
import { DocumentValues, ResponseDemandDocument } from '../types/document';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import { toast } from 'react-toastify';
import { appendToFormData } from 'utils/globalMethodes';
import * as Yup from 'yup';
import useFetchData from 'hooks/useFetch';
import { getCollaborateurs } from 'services/filtersService';
import { EnumProjectModule } from 'features/project/enums/enums';
import { getProjectsUser } from 'features/project/services/services';
import { useAuthContext } from 'contexts/AuthContext';
import MintErrorMessage from 'components/common/MintErrorMessage';

interface CreateDocumentParams {
  id?: number;
  refetch?: () => void;
  userId?: string;
  initialValues?: DocumentValues;
}

const CreateDocument: React.FC<SideBarProps<CreateDocumentParams>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const [data, setData] = useState<ResponseDemandDocument | null>(null);
  const [users, setUsers] = useState<SelectItem[]>([]);
  const [documentsType, setDocumentsType] = useState<SelectItem[]>([]);
  const [projectsList, setProjectData] = useState<SelectItem[]>([]);
  const { id } = useParams();
  const { closeSidebar } = useSidebarManager();
  const { data: typelist } = useFetchData(documentsTypelist, []);
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);

  const handleFetchData = async () => {
    try {
      setLoading(true);

      const [collabResponse, docListResponse] = await Promise.all([
        getCollaborateurs(),
        documentTypesService(),
      ]);

      if (collabResponse.success) {
        setUsers(collabResponse.data);
      } else {
        toast.error(collabResponse.message);
        return closeSidebar(CreateDocument);
      }

      if (docListResponse.success) {
        setDocumentsType(docListResponse.data);
      } else {
        toast.error(docListResponse.message);
        return closeSidebar(CreateDocument);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFetchDetails = async () => {
    const response = await GetCreateDocument(params.id as number);
    if (response) {
      setData(response);
      setUsers(response.userslist);
      setDocumentsType(response.typeslist);
      fetchProjectData(response.userid);
    } else {
      closeSidebar(CreateDocument);
    }
  };

  useEffect(() => {
    if (params.id) {
      handleFetchDetails();
    } else {
      handleFetchData();
      const userId = params?.userId ?? user.userId;
      if (userId) {
        fetchProjectData(userId);
      }
    }
  }, []);

  const validationSchema = Yup.object().shape({
    typeId: Yup.string().required(t('RequiredAttribute')),
    userid: Yup.string().required(t('RequiredAttribute')),
  });

  const handleSubmit = async (
    values: DocumentValues,
    { setStatus, setSubmitting }: FormikHelpers<DocumentValues>,
  ): Promise<void> => {
    try {
      setSubmitting(true);
      // when the user select is hidden the owner is imposed by the caller,
      // otherwise the selected value wins over any fallback
      const resolvedUserId =
        params?.userId || values.userid || (id as string) || user.userId;
      if (!resolvedUserId) {
        setStatus(t('DocDemand.Error.MissingUser'));
        return;
      }

      const response = await CreateDemondeDocument(
        appendToFormData({ ...values, userid: resolvedUserId }),
      );

      if (response.success) {
        toast.success(t('Default.succesMessage'));
        params?.refetch?.();
        closeSidebar(CreateDocument);
      } else {
        setStatus(response.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const checkIfHasDigitalVersion = (id: number): boolean => {
    return typelist?.some((t) => Number(t.value) == id && t.text == 'True')
      ? true
      : false;
  };

  const fetchProjectData = async (userId: string) => {
    const response = await getProjectsUser({
      userId: userId,
      module: EnumProjectModule.Documents,
    });
    if (response.success) {
      setProjectData(response.data);
    }
  };

  const getInitialValues = (): DocumentValues => {
    if (params.id && data) {
      return data as unknown as DocumentValues;
    }

    return {
      id: 0,
      remark: '',
      typeId: documentsType.length > 0 ? Number(documentsType[0].value) : null,
      version: 1,
      userid: id?.toString() ?? params?.userId ?? user.userId ?? '',
      processingDate: new Date().toISOString(),
      projectId: null,
    };
  };

  if ((params.id && !data) || loading) {
    return (
      <Sidebar
        size="m"
        type="primary"
        sidebar={sidebar}
        setSidebarWidth={setSidebarWidth}
        sidebarWidth={sidebarWidth}
      >
        <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-4 minthr-border-b minthr-border-b-3">
          <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
            <img className="minthr-icon-m" src="/icons/stopwatch.svg" alt="" />
          </div>
          <h3>{t('DocDemand.EditModal.Title')}</h3>
          <button
            onClick={() => closeSidebar(CreateDocument)}
            className="minthr-sidebar-close"
            type="button"
          >
            <img src="/icons/close.svg" alt="" />
          </button>
        </div>
        <div className="minthr-flex minthr-flex-items-center minthr-flex-justify-center minthr-py-5 minthr-h-100">
          <Loader />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar
      size="m"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-4 minthr-border-b minthr-border-b-3">
        <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
          <img className="minthr-icon-m" src="/icons/stopwatch.svg" alt="" />
        </div>
        <h3>
          {params.id
            ? t('DocDemand.EditModal.Title')
            : t('DocDemand.CreateModal.Title')}
        </h3>
        <button
          onClick={() => closeSidebar(CreateDocument)}
          className="minthr-sidebar-close"
          type="button"
        >
          <img src="/icons/close.svg" alt="" />
        </button>
      </div>

      <Formik
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, values, status, isSubmitting }) => (
          <Form>
            <div className="minthr-pt-2 minthr-flex minthr-flex-column">
              {status && <MintErrorMessage message={status} />}

              {/* Type Select */}
              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12 document-sidebar-step1">
                  <MinthrSelect
                    name="typeId"
                    label={t('DocDemand.F.Type')}
                    options={documentsType.map((option) => ({
                      label: option.text,
                      value: option.value,
                    }))}
                  />
                </div>
              </div>

              {/* Users */}
              {!params?.userId && (
                <div className="row g-0 minthr-mb-5">
                  <div className="col-sm-12">
                    <MinthrSelect
                      name="userid"
                      value={values.userid}
                      label={t('DocDemand.F.Users')}
                      options={
                        users?.map((item) => ({
                          label: item.text,
                          value: item.value,
                        })) ?? []
                      }
                      onchange={(e: SelectOptionResponse) => {
                        setFieldValue('userid', e.value);
                        setFieldValue('projectId', '');
                        setProjectData([]);
                        fetchProjectData(e.value as string);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Projects */}
              {projectsList && projectsList.length > 0 && (
                <div className="row g-0 minthr-mb-5">
                  <div className="col-sm-12">
                    <MinthrSelect
                      key={`project-${values.userid}-${projectsList.length}`}
                      options={projectsList.map((item) => ({
                        label: item.text,
                        value: item.value,
                      }))}
                      name="projectId"
                      label={t('ProjectName')}
                      onchange={(e: SelectOptionResponse) => {
                        setFieldValue('projectId', e.value);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Version */}
              <div className="row g-0 minthr-mb-5">
                <div className="minthr-flex minthr-flex-space-between document-sidebar-step2">
                  <label
                    className={`minthr-btn-checkbox minthr-btn-outline-checkbox ${
                      checkIfHasDigitalVersion(Number(values.typeId))
                        ? 'minthr-mr-2 w-50'
                        : 'w-100'
                    }`}
                  >
                    <input
                      type="radio"
                      name="Version"
                      value="1"
                      required
                      checked={values.version === 1 || values.version === 0}
                      onChange={() => setFieldValue('version', 1)}
                    />
                    <span>{t('version.digital')}</span>
                  </label>

                  {checkIfHasDigitalVersion(Number(values.typeId)) && (
                    <label className="minthr-btn-checkbox minthr-btn-outline-checkbox minthr-ml-2 w-50">
                      <input
                        type="radio"
                        name="Version"
                        value="2"
                        required
                        checked={values.version === 2}
                        onChange={() => setFieldValue('version', 2)}
                      />
                      <span>{t('version.papier')}</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Remark */}
              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12 document-sidebar-step3">
                  <TextArea label={t('DocDemand.F.Remark')} name="remark" />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="row minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-py-4 minthr-mx-n5 minthr-px-4 minthr-mt-auto">
              <Button
                type="submit"
                className="col minthr-btn minthr-btn-primary minthr-ml-2 document-sidebar-step4"
                loading={isSubmitting}
              >
                {t('submit')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Sidebar>
  );
};

export default CreateDocument;
